// @djsem/shared — Library Landscape / Scatter Map (Prompt 11).
//
// A DJSEM-native 2D spatial view of the library for Track Library / Prep / Set Builder.
// Reuses DJSEMLibraryTrack (Prompt 06) and DJSEMTrackGraphFilter (Prompt 10) — no duplicate
// compatibility scoring. Every point preserves its source values + warnings; missing data
// projects to a midpoint WITH a warning (never invented, never a misleading science claim).
// Prep-side only — the live conductor never runs library exploration.

import {
  vArray, vNumber, vObject, vString, vUnion, vRefine, type Validator,
  type DJSEMValidationResult, toDJSEMValidationResult,
} from "./validation";
import type { DJSEMLibraryTrack } from "./smartGroups";
import type { DJSEMTrackGraphFilter } from "./trackGraph";

// ---- contracts --------------------------------------------------------------

/** A quantitative axis a library scatter-map point can be projected onto. */
export type DJSEMLibraryMapAxis =
  | "bpm" | "energy" | "semantic_brightness" | "semantic_darkness"
  | "mood_intensity" | "approval_readiness" | "transition_risk" | "manual";

/** All known {@link DJSEMLibraryMapAxis} values, in canonical order. */
export const DJSEM_LIBRARY_MAP_AXES: DJSEMLibraryMapAxis[] = [
  "bpm", "energy", "semantic_brightness", "semantic_darkness",
  "mood_intensity", "approval_readiness", "transition_risk", "manual",
];

/** A single track projected onto the 2D library scatter map. */
export interface DJSEMLibraryMapPoint {
  preparationTrackId: string;
  title: string;
  artist?: string;
  x: number;
  y: number;
  size: number;
  label: string;
  colorToken?: string;
  approvedForLive: boolean;
  warnings: string[];
  sourceValues: Record<string, unknown>;
}

/** A computed grouping of nearby/related points on the library scatter map. */
export interface DJSEMLibraryMapCluster {
  id: string;
  label: string;
  pointIds: string[];
  centroidX: number;
  centroidY: number;
  reason: string;
  warnings: string[];
}

/** The full computed state of a library scatter map: axes, points, clusters, and active filters. */
export interface DJSEMLibraryMapState {
  id: string;
  xAxis: DJSEMLibraryMapAxis;
  yAxis: DJSEMLibraryMapAxis;
  points: DJSEMLibraryMapPoint[];
  clusters: DJSEMLibraryMapCluster[];
  filters: DJSEMTrackGraphFilter[];
  warnings: string[];
}

/** Options controlling how {@link LibraryLandscapeService.buildMap} projects and clusters tracks. */
export interface LibraryMapOptions {
  xAxis: DJSEMLibraryMapAxis;
  yAxis: DJSEMLibraryMapAxis;
  bpmMin?: number;         // normalization window (defaults 60..200)
  bpmMax?: number;
  clusterMode?: "grid" | "semantic_tag" | "none";
  gridSize?: number;       // buckets per axis for grid clustering
  transitionRiskById?: Record<string, "low" | "medium" | "high" | "unknown">;
  manualX?: Record<string, number>;
  manualY?: Record<string, number>;
}

// Well-known bright/dark semantic vocabulary. Conservative; only used if the track already
// carries these tags — never invented from nothing.
const BRIGHT = new Set(["euphoric", "uplifting", "bright", "triumph", "joy", "warm", "day", "hopeful"]);
const DARK = new Set(["dark", "melancholic", "aggressive", "night", "tense", "cold", "brooding"]);

// ---- validator --------------------------------------------------------------

const vAxis: Validator = vUnion(...DJSEM_LIBRARY_MAP_AXES.map((a) => vString({ enum: [a] })));
const vPoint: Validator = vObject(
  {
    preparationTrackId: vString({ min: 1 }), title: vString({ min: 1 }),
    x: vNumber({ min: 0, max: 1 }), y: vNumber({ min: 0, max: 1 }), size: vNumber({ min: 0, max: 1 }),
    label: vString({}), approvedForLive: (v) => (typeof v === "boolean" ? { valid: true, issues: [] } : { valid: false, issues: [{ path: "$", message: "expected boolean" }] }),
    warnings: vArray(vString({})), sourceValues: vObject({}, {}, { open: true }),
  },
  { artist: vString({}), colorToken: vString({}) },
);

/** Validator for {@link DJSEMLibraryMapState}, including a duplicate-point-id refinement. */
export const vLibraryMapState: Validator<DJSEMLibraryMapState> = vRefine(
  vObject({
    id: vString({ min: 1 }), xAxis: vAxis, yAxis: vAxis,
    points: vArray(vPoint), clusters: vArray((_v) => ({ valid: true, issues: [] })),
    filters: vArray((_v) => ({ valid: true, issues: [] })), warnings: vArray(vString({})),
  }) as Validator<DJSEMLibraryMapState>,
  (m) => {
    const ids = new Set<string>();
    for (const p of m.points) {
      if (ids.has(p.preparationTrackId)) return `duplicate point id: ${p.preparationTrackId}`;
      ids.add(p.preparationTrackId);
    }
    return null;
  },
);

// ---- service (class-based; hard rule 12) ------------------------------------

const clampUnit = (n: number) => Math.max(0, Math.min(1, Number.isFinite(n) ? n : 0.5));
const round = (n: number) => Math.round(n * 1000) / 1000;

/**
 * Builds and clusters a 2D spatial "scatter map" view of the library for Track Library /
 * Prep / Set Builder. Missing source data always projects to an axis midpoint with a
 * warning — never invented or silently hidden (prep-side only; never used live).
 */
export class LibraryLandscapeService {
  /**
   * Projects a set of library tracks onto the chosen axes and clusters the resulting points.
   * @param tracks - The library tracks to plot.
   * @param options - The x/y axes and clustering configuration.
   * @returns The full map state (points, clusters, warnings).
   */
  buildMap(tracks: DJSEMLibraryTrack[], options: LibraryMapOptions): DJSEMLibraryMapState {
    const warnings: string[] = [];
    if (tracks.length === 0) warnings.push("Library is empty; no points to plot.");
    // Deterministic: order points by id.
    const ordered = [...tracks].sort((a, b) => a.preparationTrackId.localeCompare(b.preparationTrackId));
    const points = ordered.map((t) => this.projectTrackToPoint(t, options));
    const clusters = this.clusterPoints(points, options);
    return {
      id: `map_${options.xAxis}_${options.yAxis}_${points.length}`,
      xAxis: options.xAxis, yAxis: options.yAxis, points, clusters, filters: [], warnings,
    };
  }

  /**
   * Projects a single track onto the chosen x/y axes, recording a warning wherever source
   * data is missing (and falling back to the axis midpoint).
   * @param track - The library track to project.
   * @param options - The x/y axes and normalization/manual-coordinate configuration.
   * @returns The resulting map point.
   */
  projectTrackToPoint(track: DJSEMLibraryTrack, options: LibraryMapOptions): DJSEMLibraryMapPoint {
    const warnings: string[] = [];
    const sourceValues: Record<string, unknown> = {
      bpm: track.bpm, energyScore: track.energyScore, reviewStatus: track.reviewStatus,
      approvedForLive: track.approvedForLive, moodTags: track.moodTags, semanticDomains: track.semanticDomains,
    };
    const x = this.axisValue(track, options.xAxis, options, warnings, "x");
    const y = this.axisValue(track, options.yAxis, options, warnings, "y");
    // Point size encodes approval readiness (approved larger), never hides unapproved.
    const size = clampUnit(track.approvedForLive ? 0.8 : 0.45);
    return {
      preparationTrackId: track.preparationTrackId,
      title: track.title ?? track.preparationTrackId,
      artist: track.artist,
      x: round(x), y: round(y), size,
      label: track.title ?? track.preparationTrackId,
      colorToken: track.approvedForLive ? "approved" : (track.reviewStatus === "rejected" ? "rejected" : "draft"),
      approvedForLive: !!track.approvedForLive,
      warnings,
      sourceValues,
    };
  }

  /**
   * Clusters already-projected points according to the configured cluster mode.
   * @param points - The points to cluster.
   * @param options - Controls the cluster mode (`"grid"`, `"semantic_tag"`, or `"none"`) and grid size.
   * @returns The resulting clusters (empty if mode is `"none"` or there are no points).
   */
  clusterPoints(points: DJSEMLibraryMapPoint[], options: LibraryMapOptions): DJSEMLibraryMapCluster[] {
    const mode = options.clusterMode ?? "grid";
    if (mode === "none" || points.length === 0) return [];
    if (mode === "semantic_tag") return this.clusterBySemanticTag(points);
    return this.clusterByGrid(points, options.gridSize ?? 3);
  }

  /**
   * Validates an arbitrary value against the {@link DJSEMLibraryMapState} shape.
   * @param map - The value to validate.
   * @returns The validation result.
   */
  validateMapState(map: unknown): DJSEMValidationResult {
    return toDJSEMValidationResult(vLibraryMapState(map), "library_map");
  }

  // ---- private --------------------------------------------------------------

  private axisValue(track: DJSEMLibraryTrack, axis: DJSEMLibraryMapAxis, options: LibraryMapOptions, warnings: string[], which: "x" | "y"): number {
    switch (axis) {
      case "bpm": {
        if (typeof track.bpm !== "number") { warnings.push(`Missing BPM for ${track.preparationTrackId}; placed at axis midpoint.`); return 0.5; }
        const lo = options.bpmMin ?? 60, hi = options.bpmMax ?? 200;
        return clampUnit((track.bpm - lo) / Math.max(1, hi - lo));
      }
      case "energy": {
        if (typeof track.energyScore !== "number") { warnings.push(`Missing energy for ${track.preparationTrackId}; placed at midpoint.`); return 0.5; }
        return clampUnit(track.energyScore);
      }
      case "approval_readiness": {
        if (track.approvedForLive) return 0.9;
        if (track.reviewStatus === "rejected") return 0.1;
        if (track.reviewStatus === "reviewed" || track.reviewStatus === "needs_review") return 0.5;
        return 0.3;
      }
      case "semantic_brightness": {
        const v = this.tagScore(track, BRIGHT);
        if (v === null) { warnings.push(`No semantic/mood tags for ${track.preparationTrackId}; brightness unknown (midpoint).`); return 0.5; }
        return v;
      }
      case "semantic_darkness": {
        const v = this.tagScore(track, DARK);
        if (v === null) { warnings.push(`No semantic/mood tags for ${track.preparationTrackId}; darkness unknown (midpoint).`); return 0.5; }
        return v;
      }
      case "mood_intensity": {
        const n = (track.moodTags ?? []).length;
        if (n === 0) { warnings.push(`No mood tags for ${track.preparationTrackId}; intensity unknown (midpoint).`); return 0.5; }
        return clampUnit(Math.min(1, n / 3));
      }
      case "transition_risk": {
        const r = options.transitionRiskById?.[track.preparationTrackId];
        if (!r || r === "unknown") { warnings.push(`Transition risk unknown for ${track.preparationTrackId}.`); return 0.5; }
        return r === "low" ? 0.15 : r === "medium" ? 0.5 : 0.85;
      }
      case "manual": {
        const map = which === "x" ? options.manualX : options.manualY;
        const v = map?.[track.preparationTrackId];
        if (typeof v !== "number") { warnings.push(`No manual ${which} coordinate for ${track.preparationTrackId}.`); return 0.5; }
        return clampUnit(v);
      }
      default: return 0.5;
    }
  }

  private tagScore(track: DJSEMLibraryTrack, vocab: Set<string>): number | null {
    const tags = [...(track.moodTags ?? []), ...(track.semanticDomains ?? []), ...(track.semanticCategories ?? [])].map((s) => s.toLowerCase());
    if (tags.length === 0) return null;
    const hits = tags.filter((t) => vocab.has(t)).length;
    return clampUnit(Math.min(1, 0.2 + hits * 0.4)); // presence-weighted, capped
  }

  private clusterByGrid(points: DJSEMLibraryMapPoint[], gridSize: number): DJSEMLibraryMapCluster[] {
    const buckets = new Map<string, DJSEMLibraryMapPoint[]>();
    for (const p of points) {
      const gx = Math.min(gridSize - 1, Math.floor(p.x * gridSize));
      const gy = Math.min(gridSize - 1, Math.floor(p.y * gridSize));
      const key = `${gx}_${gy}`;
      (buckets.get(key) ?? buckets.set(key, []).get(key)!).push(p);
    }
    return [...buckets.entries()].sort((a, b) => a[0].localeCompare(b[0])).map(([key, pts], i) => ({
      id: `cluster_grid_${key}`,
      label: `Region ${key}`,
      pointIds: pts.map((p) => p.preparationTrackId),
      centroidX: round(avg(pts.map((p) => p.x))), centroidY: round(avg(pts.map((p) => p.y))),
      reason: `Grid bucket ${key}: ${pts.length} track(s) grouped by proximity on the chosen axes.`,
      warnings: [],
    }));
  }

  private clusterBySemanticTag(points: DJSEMLibraryMapPoint[]): DJSEMLibraryMapCluster[] {
    const byTag = new Map<string, DJSEMLibraryMapPoint[]>();
    for (const p of points) {
      const tags = (p.sourceValues.moodTags as string[] | undefined) ?? [];
      const tag = tags[0] ?? "untagged";
      (byTag.get(tag) ?? byTag.set(tag, []).get(tag)!).push(p);
    }
    return [...byTag.entries()].sort((a, b) => a[0].localeCompare(b[0])).map(([tag, pts]) => ({
      id: `cluster_tag_${tag}`,
      label: tag === "untagged" ? "Untagged" : `Mood: ${tag}`,
      pointIds: pts.map((p) => p.preparationTrackId),
      centroidX: round(avg(pts.map((p) => p.x))), centroidY: round(avg(pts.map((p) => p.y))),
      reason: tag === "untagged" ? "Tracks without mood tags." : `Grouped by shared mood tag "${tag}".`,
      warnings: tag === "untagged" ? ["These tracks lack mood tags; grouping is by absence, not similarity."] : [],
    }));
  }
}

function avg(xs: number[]): number { return xs.length ? xs.reduce((a, b) => a + b, 0) / xs.length : 0; }
